import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Fuel,
  CircuitBoard,
  Building2,
  BookOpen,
  Blocks,
  GraduationCap,
  Presentation,
  Briefcase,
  Atom,
  Cog,
  Mail,
  Plus,
  X
} from "lucide-react";
import { Icons } from "@/components/Icons";
import { profileDatabase, getAllProfileNames, getProfileByName } from "@/services/profileDataService";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  role: z.string().min(2, { message: "Role must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  bio: z.string().optional(),
  domains: z.array(z.string()).min(1, { message: "Add at least one domain of expertise" }),
  industries: z.array(
    z.object({
      name: z.string(),
      segment: z.string(),
      icon: z.string()
    })
  ).min(1, { message: "Add at least one industry" }),
});

const availableIcons = [
  { name: "Fuel", icon: Fuel },
  { name: "CircuitBoard", icon: CircuitBoard },
  { name: "Building", icon: Building2 },
  { name: "Book", icon: BookOpen },
  { name: "Blocks", icon: Blocks },
  { name: "Graduation", icon: GraduationCap },
  { name: "Presentation", icon: Presentation },
  { name: "Briefcase", icon: Briefcase },
  { name: "Atom", icon: Atom },
  { name: "Cog", icon: Cog },
];

const industryOptions = [
  "Select your industry to get started",
  "Agriculture & Natural Resources",
  "Aerospace, Aviation & Defense",
  "Energy & Utilities",
  "Manufacturing & Industrial Production",
  "Capital Projects",
  "Construction",
  "Real Estate & Property Development",
  "Consumer Goods & Services",
  "Financial Services & Investments",
  "Technology & Services",
  "Logistics & Transportation",
  "Media, Marketing, & Entertainment",
  "Healthcare & Medical Services",
  "Professional Services",
  "Government",
  "Other"
];

const CreateProfile = () => {
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [industrySegment, setIndustrySegment] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      role: "",
      email: "",
      bio: "",
      domains: [],
      industries: [],
    },
  });

  const domains = form.watch("domains");
  const industries = form.watch("industries");
  const formValues = form.watch();

  // Auto-populate form when profile is selected
  useEffect(() => {
    if (selectedProfile) {
      const profileData = getProfileByName(selectedProfile);
      if (profileData) {
        form.setValue("name", profileData.name);
        form.setValue("role", profileData.role);
        form.setValue("email", profileData.email);
        form.setValue("bio", profileData.bio);
        form.setValue("domains", profileData.domains);
        form.setValue("industries", profileData.industries);
      }
    }
  }, [selectedProfile, form]);

  // Check if all required fields are filled
  const isFormComplete = () => {
    return (
      formValues.name &&
      formValues.role &&
      formValues.email &&
      formValues.domains.length > 0 &&
      formValues.industries.length > 0
    );
  };

  const addDomain = () => {
    if (domainInput.trim() && !domains.includes(domainInput.trim())) {
      form.setValue("domains", [...domains, domainInput.trim()]);
      setDomainInput("");
    }
  };

  const removeDomain = (index: number) => {
    form.setValue(
      "domains",
      domains.filter((_, i) => i !== index)
    );
  };

  const addIndustry = () => {
    if (selectedIndustry && selectedIndustry !== "Select your industry to get started" && industrySegment.trim()) {
      const newIndustry = {
        name: selectedIndustry,
        segment: industrySegment.trim(),
        icon: "Fuel", // Default icon since we removed the icon selector
      };
      
      // Check if industry already exists
      const exists = industries.some(
        industry => industry.name.toLowerCase() === newIndustry.name.toLowerCase() && 
        industry.segment.toLowerCase() === newIndustry.segment.toLowerCase()
      );
      
      if (!exists) {
        form.setValue("industries", [...industries, newIndustry]);
        setSelectedIndustry("");
        setIndustrySegment("");
      }
    }
  };

  const removeIndustry = (index: number) => {
    form.setValue(
      "industries",
      industries.filter((_, i) => i !== index)
    );
  };

  const generateProfileImage = async () => {
    if (!selectedProfile) {
      toast.error("Please select a profile first");
      return;
    }

    setIsGeneratingImage(true);
    
    const profileData = getProfileByName(selectedProfile);
    if (profileData) {
      // Simulate generating an image with Giblet and use the profile's image
      setTimeout(() => {
        setProfileImage(profileData.profileImage);
        setIsGeneratingImage(false);
        toast.success("GibNifty generated successfully!");
      }, 2000);
    }
  };

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (!profileImage) {
      toast.error("Please generate a GibNifty profile image");
      return;
    }
    
    if (!selectedProfile) {
      toast.error("Please select a profile");
      return;
    }
    
    console.log("Form submitted:", data);
    
    const profileData = getProfileByName(selectedProfile);
    if (profileData) {
      toast.success("mySPAI Profile created successfully!");
      navigate(profileData.profileRoute);
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Create Your mySPAI Profile</h1>
          <p className="text-gray-600">Fill out the form below to create your personalized profile</p>
        </div>

        {/* Profile Selector */}
        <Card className="p-6 mb-6">
          <h3 className="font-sans text-xl font-semibold text-black mb-3">Select Profile to Demo</h3>
          <Select value={selectedProfile} onValueChange={setSelectedProfile}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Choose a team member profile to demo" />
            </SelectTrigger>
            <SelectContent>
              {getAllProfileNames().map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Card>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 col-span-2">
                <div className="flex items-start space-x-6">
                  <div className="flex flex-col items-center gap-4">
                    <Avatar className="h-36 w-36">
                      {profileImage ? (
                        <AvatarImage src={profileImage} alt="Profile" />
                      ) : (
                        <AvatarFallback>
                          <Plus className="h-8 w-8 text-gray-400" />
                        </AvatarFallback>
                      )}
                    </Avatar>
                    
                    <Button
                      type="button"
                      variant="outline"
                      onClick={generateProfileImage}
                      disabled={isGeneratingImage || !isFormComplete()}
                      className="w-full"
                    >
                      {isGeneratingImage ? (
                        <>Generating with Giblet</>
                      ) : (
                        <>Generate GibNifty</>
                      )}
                    </Button>
                    
                    <div className="w-full">
                      <h3 className="font-sans text-xl font-semibold text-black mb-3">Industries</h3>
                      
                      <div className="space-y-4">
                        <div className="flex gap-2">
                          <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                            <SelectTrigger className="flex-grow">
                              <SelectValue placeholder="Select your industry to get started" />
                            </SelectTrigger>
                            <SelectContent>
                              {industryOptions.slice(1).map((industry) => (
                                <SelectItem key={industry} value={industry}>
                                  {industry}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="flex gap-2">
                          <Input
                            placeholder="Industry segment"
                            value={industrySegment}
                            onChange={(e) => setIndustrySegment(e.target.value)}
                            className="flex-grow"
                          />
                          
                          <Button
                            type="button"
                            variant="outline"
                            onClick={addIndustry}
                            className="flex-shrink-0"
                          >
                            Add
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          {industries.map((industry, index) => {
                            const IconComponent = availableIcons.find(
                              (i) => i.name === industry.icon
                            )?.icon || Fuel;
                            
                            return (
                              <div
                                key={index}
                                className="flex items-center justify-between p-3 border-2 border-black rounded-lg"
                              >
                                <div className="flex items-center space-x-2">
                                  <IconComponent className="h-5 w-5 text-black" />
                                  <div className="flex flex-col">
                                    <span className="text-sm font-medium text-black">
                                      {industry.name}
                                    </span>
                                    <span className="text-xs text-gray-600">
                                      {industry.segment}
                                    </span>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => removeIndustry(index)}
                                  className="h-8 w-8 p-0"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your full name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Role / Title</FormLabel>
                          <FormControl>
                            <Input placeholder="Your professional role" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="your.email@example.com"
                              type="email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="bio"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bio</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="A brief description about yourself"
                              className="h-24"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </Card>
              
              <div className="space-y-6">
                <Card className="p-6">
                  <h3 className="font-sans text-xl font-semibold text-black mb-3">
                    Domains of Expertise
                  </h3>
                  
                  <div className="flex items-center space-x-2 mb-4">
                    <Input
                      placeholder="Add a domain"
                      value={domainInput}
                      onChange={(e) => setDomainInput(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addDomain}
                      className="flex-shrink-0"
                    >
                      Add
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {domains.map((domain, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="flex items-center gap-1 border-2 border-black text-black py-1 px-2"
                      >
                        {domain}
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removeDomain(index)}
                          className="h-4 w-4 p-0 ml-1"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                  {form.formState.errors.domains && (
                    <p className="text-sm text-red-500 mt-2">
                      {form.formState.errors.domains.message as string}
                    </p>
                  )}
                </Card>
                
                <Card className="p-6 bg-gray-100">
                  <h3 className="font-sans text-xl font-semibold text-black mb-3">
                    Profile Preview
                  </h3>
                  <p className="text-sm text-gray-600">
                    Your profile will be visible to other users in the iSPAI network. Fill out all fields to get the most out of your profile.
                  </p>
                </Card>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button type="submit" className="bg-[#0f172a] hover:bg-[#1e293b] text-white">
                Create mySPAI Profile
              </Button>
            </div>
          </form>
        </Form>
      </main>
    </div>
  );
};

export default CreateProfile;
